import '@stimulus/polyfills'
import { Application, Controller } from 'stimulus'
import Cookies from 'js-cookie'

const app = Application.start()

const COOKIE_NAME = 'refosumu_bookmarked_subsidy_ids'
const COOKIE_EXPIRES_DAYS = 365
const MAX_BOOKMARKS = 50

app.register('refosumu--shared--subsidy-bookmark-button', class extends Controller {
  static targets = ['icon', 'iconOff', 'iconOn']
  static values = {
    area: String,
    id: Number,
    name: String,
    key: String,
  }

  connect() {
    this.updateUI()
    // 他のブックマークボタンとの同期のためにイベントをリッスン
    this.boundHandleBookmarkUpdated = this.handleBookmarkUpdated.bind(this)
    window.addEventListener('bookmark-updated', this.boundHandleBookmarkUpdated)
  }

  disconnect() {
    window.removeEventListener('bookmark-updated', this.boundHandleBookmarkUpdated)
  }

  handleBookmarkUpdated(event) {
    // 同じ補助金のブックマーク状態が変更された場合にUIを更新
    if (event.detail.key === this.keyValue) {
      this.updateUI()
    }
  }

  toggle(event) {
    // カードのリンククリックを防ぐ
    event.preventDefault()
    event.stopPropagation()

    const isBookmarked = this.isBookmarked()
    let success = false

    if (isBookmarked) {
      success = this.removeBookmark()
      if (success) {
        this.sendGA4Event('remove_from_bookmark')
      }
    } else {
      success = this.addBookmark()
      if (success) {
        this.sendGA4Event('add_to_bookmark')
      }
    }

    if (success) {
      this.updateUI()
      this.notifyOtherButtons()
    }
  }

  isBookmarked() {
    const bookmarks = this.getBookmarks()
    return bookmarks.some(b => b.area === this.areaValue && b.id === this.idValue)
  }

  getBookmarks() {
    try {
      const cookieValue = Cookies.get(COOKIE_NAME)
      if (!cookieValue) {
        return []
      }
      return JSON.parse(cookieValue)
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('ブックマークの読み込みエラー:', error)
      }
      return []
    }
  }

  saveBookmarks(bookmarks) {
    try {
      const isSecure = process.env.NODE_ENV !== 'development'
      Cookies.set(COOKIE_NAME, JSON.stringify(bookmarks), {
        expires: COOKIE_EXPIRES_DAYS,
        secure: isSecure,
        sameSite: 'Lax',
      })
      return true
    } catch (error) {
      console.warn('ブックマークの保存エラー:', error)
      return false
    }
  }

  addBookmark() {
    const bookmarks = this.getBookmarks()

    // 既に存在する場合は追加しない
    if (bookmarks.some(b => b.area === this.areaValue && b.id === this.idValue)) {
      return true
    }

    // 上限に達している場合は最も古いブックマークを削除
    if (bookmarks.length >= MAX_BOOKMARKS) {
      bookmarks.shift()
    }

    bookmarks.push({
      area: this.areaValue,
      id: this.idValue,
      saved_at: new Date().toISOString(),
    })

    return this.saveBookmarks(bookmarks)
  }

  removeBookmark() {
    const bookmarks = this.getBookmarks()
    const filteredBookmarks = bookmarks.filter(
      b => !(b.area === this.areaValue && b.id === this.idValue)
    )
    return this.saveBookmarks(filteredBookmarks)
  }

  updateUI() {
    const isBookmarked = this.isBookmarked()

    if (isBookmarked) {
      this.element.classList.add('shared-subsidy-bookmark-button--active')
      this.element.setAttribute('aria-pressed', 'true')
      this.element.setAttribute('aria-label', 'この補助金の保存を解除')
    } else {
      this.element.classList.remove('shared-subsidy-bookmark-button--active')
      this.element.setAttribute('aria-pressed', 'false')
      this.element.setAttribute('aria-label', 'この補助金を保存')
    }
  }

  notifyOtherButtons() {
    // 同じページ内の他のブックマークボタンに状態変更を通知
    window.dispatchEvent(new CustomEvent('bookmark-updated', {
      detail: { key: this.keyValue }
    }))
  }

  sendGA4Event(eventName) {
    if (typeof window.dataLayer === 'undefined') {
      return
    }

    window.dataLayer.push({
      event: eventName,
      subsidy_id: this.idValue,
      subsidy_area: this.areaValue,
      subsidy_name: this.nameValue,
    })
  }
})
